<template>
    <div
        class="@container/mh-sidebar flex-1 min-h-0 max-h-full flex flex-col rounded-2xl border-x-0 border-t-0 border-b border-b-white md:bg-slate-300/30 md:shadow-[inset_0px_2px_4px_0px_rgba(0,0,0,0.06)] md:border-solid"
    >
        <div
            class="fixed inset-s-0 inset-e-0 inset-be-0 z-10 flex flex-col gap-3 p-4 border border-solid border-white shadow-lg bg-white/80 backdrop-blur-sm min-[380px]:max-md:rounded-[26px] min-[380px]:inset-s-4 min-[380px]:inset-e-4 min-[380px]:inset-be-4 md:relative md:inset-s-auto md:inset-e-auto md:inset-be-auto md:bg-slate-50 md:rounded-t-2xl md:rounded-b-none"
        >
            <div class="flex items-center gap-2">
                <div class="grow">
                    <vx-filterable
                        v-if="!filterableModal"
                        @toggle="toggleFilterable"
                        :active="filterableDropdown"
                        appearance="input"
                        placement="bottom"
                        size="md"
                        scrollable
                        data-test="filters"
                    >
                        <template v-slot:placeholder>
                            <span>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="clearFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </span>
                        </template>
                        <template v-if="isMd" v-slot:form>
                            <intelligent-inbox-filters
                                v-model="filters"
                                @apply="closeFilterable"
                                @cancel="closeFilterable"
                            ></intelligent-inbox-filters>
                        </template>
                    </vx-filterable>
                </div>

                <vx-dropdown placement="right" teleport data-test="sort-items">
                    <vx-button size="md" color="muted" data-test="sort-order" aria-label="Sort order">
                        <font-awesome-icon widthAuto :icon="faArrowDownArrowUp"></font-awesome-icon>
                    </vx-button>
                    <template v-slot:items>
                        <message-hub-sidebar-sort-item
                            @click="sortForward = true"
                            :active="sortForward"
                            :icon="faArrowUp"
                            data-test="oldest-first"
                        >
                            Oldest First
                        </message-hub-sidebar-sort-item>

                        <message-hub-sidebar-sort-item
                            @click="sortForward = false"
                            :active="!sortForward"
                            :icon="faArrowDown"
                            data-test="newest-first"
                        >
                            Newest First
                        </message-hub-sidebar-sort-item>
                    </template>
                </vx-dropdown>
            </div>

            <vx-alert v-if="!isDefaultView" :closeable="false" color="info" data-test="filtered-alert">
                <div class="flex items-center justify-between">
                    <div class="grow">
                        New messages <span class="font-extrabold">will not</span> show while filters are applied.
                    </div>
                    <vx-button @click="clearFilters" color="info" size="sm" data-test="filtered-alert-clear">
                        Clear
                        <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                    </vx-button>
                </div>
            </vx-alert>
        </div>

        <vx-modal v-model:visible="filterableModal" size="sm" :padding="false">
            <template v-slot:header>
                <div class="p-6 bg-white">Filters</div>
            </template>

            <intelligent-inbox-filters
                v-if="filterableModal"
                appearance="modal"
                v-model="filters"
                @apply="closeFilterable"
                @cancel="closeFilterable"
            ></intelligent-inbox-filters>
        </vx-modal>

        <div class="scroll-thin flex-auto overflow-auto">
            <div
                v-if="!hasLoadedInitial"
                data-test="inbox-initial-skeleton"
                class="flex flex-col p-4 gap-4 pb-24 md:pb-4"
            >
                <div
                    v-for="n in 6"
                    :key="n"
                    :style="{ animationDelay: `${n * 0.15}s` }"
                    class="rounded-2xl animate-pulse bg-slate-300 h-14 w-full"
                ></div>
            </div>
            <div v-else class="flex flex-col p-4 gap-4 pb-24 md:pb-4">
                <intelligent-inbox-section
                    v-for="section in sections"
                    :key="section.sectionValue"
                    :section="section"
                    :loadingCategories="loadingCategories"
                    :loadingCounts="loadingCounts"
                    :sectionCount="sectionCounts[section.sectionValue] ?? 0"
                    :categoryCounts="categoryCounts[section.sectionValue] ?? {}"
                ></intelligent-inbox-section>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { faArrowDown, faArrowDownArrowUp, faArrowUp, faCircleXmark, faUndo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxDropdown, VxFilterable, VxModal } from '@voxie/frontend-components';
import { useIntervalFn } from '@vueuse/core';
import IntelligentInboxSection from './IntelligentInboxSection.vue';
import IntelligentInboxFilters from './IntelligentInboxFilters.vue';
import MessageHubSidebarSortItem from '~/components/message-hub/message-hub-sidebar/MessageHubSidebarSortItem.vue';
import { useIntelligentInbox } from './useIntelligentInbox';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { pluralized } from '~/components/filters';
import Bus from '~/globals/eventBus';
import type { Thread } from '~/services/threads/threadsClient.types';

const { getContext } = useCurrentGroup();
const { isMd } = useResponsiveLayout();

const {
    sections,
    categories,
    loadingCategories,
    loadingCounts,
    fetchCategories,
    refreshCategories,
    fetchCounts,
    sectionCounts,
    categoryCounts,
    refreshCounts,
    filters,
    filtersCount,
    isDefaultView,
    clearFilters,
    sortForward,
    hasLoadedInitial,
    tick,
} = useIntelligentInbox();

useIntervalFn(tick, 60_000, { immediateCallback: true });

const filterableDropdown = ref(false);
const filterableModal = ref(false);

const filterableActive = computed<boolean>({
    get: () => (isMd.value ? filterableDropdown.value : filterableModal.value),
    set: (value: boolean) => {
        if (isMd.value) {
            filterableDropdown.value = value;
        } else {
            filterableModal.value = value;
        }
    },
});

const toggleFilterable = (isActive: boolean): void => {
    filterableActive.value = isActive;
};

const closeFilterable = (): void => {
    filterableDropdown.value = false;
    filterableModal.value = false;
};

const handleEchoThreadUpdated = ({ thread, is_interaction }: { thread: Thread; is_interaction?: boolean }): void => {
    Bus.$emit('threadUpdated', { thread, isInteraction: is_interaction ?? true });
};

const handleBusThreadUpdated = (): void => {
    refreshCounts();
};

let echoChannel: EchoChannel;

onMounted(() => {
    const { teamId, orgUnitId, groupId } = getContext();
    const channelName =
        !orgUnitId && !groupId
            ? `messages.${teamId}`
            : orgUnitId
            ? `messages.${teamId}.org_unit.${orgUnitId}`
            : `messages.${teamId}.${groupId}`;
    echoChannel = window.Echo.private(channelName);
    echoChannel.listen('.ThreadUpdated', handleEchoThreadUpdated);
    Bus.$on('threadUpdated', handleBusThreadUpdated);

    if (categories.value === undefined) {
        fetchCategories();
    } else {
        refreshCategories();
    }
    fetchCounts();
});

onUnmounted(() => {
    echoChannel?.stopListening('.ThreadUpdated', handleEchoThreadUpdated);
    Bus.$off('threadUpdated', handleBusThreadUpdated);
    refreshCounts.cancel();
});
</script>

<style>
@keyframes inbox-blink {
    0%,
    100% {
        background-color: rgb(255 255 255 / 0.5);
    }
    50% {
        background-color: rgb(255 255 255 / 1);
    }
}
.animate-inbox-blink {
    animation: inbox-blink 0.6s ease-in-out 2;
}
</style>
